import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaBookOpen, FaFolderOpen, FaChalkboardTeacher } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const REASONS = [
  {
    key: 'topic',
    Icon: FaBookOpen,
    title: 'Start from a topic or your files',
    body: 'Type what you want to learn, or upload the material you already have.',
  },
  {
    key: 'structure',
    Icon: FaFolderOpen,
    title: 'Get a real course structure',
    body: 'Modules, lessons, practice, and checks. Enough to study or teach without rebuilding from scratch.',
  },
  {
    key: 'edit',
    Icon: FaChalkboardTeacher,
    title: 'Edit before you share',
    body: 'Change the content, format, and flow. You stay in control of what learners see.',
  },
];

const SlideFive = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <main className="w-full bg-white dark:bg-slate-950">
      <div className="relative overflow-hidden border-t border-slate-200 dark:border-slate-800">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/40 to-white dark:from-gray-950 dark:via-blue-950/10 dark:to-gray-950" />
        <div className="relative px-4 py-12 md:py-16 max-w-7xl mx-auto">
          <section className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight tracking-tight">
              {t('landing.slideFive.header.title', 'Ready when you are')}
            </h2>
            <p className="text-base sm:text-lg md:text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {t(
                'landing.slideFive.header.subtitle',
                'No waitlist theater. Create a course from a topic or your documents, then edit it until it fits.',
              )}
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-12">
            {REASONS.map((item, i) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="p-6 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
              >
                <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-4">
                  <item.Icon className="text-lg" aria-hidden />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">
                  {t(`landing.slideFive.reasons.${item.key}.title`, item.title)}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {t(`landing.slideFive.reasons.${item.key}.body`, item.body)}
                </p>
              </motion.div>
            ))}
          </div>

          <section className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 md:p-10 rounded-3xl border-2 border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/20"
            >
              <h3 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white mb-3 tracking-tight">
                {t('landing.slideFive.cta.title', 'Create your first course')}
              </h3>
              <p className="text-base text-gray-600 dark:text-gray-400 mb-7 max-w-xl mx-auto">
                {t(
                  'landing.slideFive.cta.subtitle',
                  'Free to start. Bring a topic or a few documents and see what Coursolo builds.',
                )}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <motion.button
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => navigate('/signup')}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/25 transition-colors"
                >
                  {t('landing.slideFive.cta.button', 'Get started free')}
                  <FaArrowRight className="text-xs" aria-hidden />
                </motion.button>
                <button
                  type="button"
                  onClick={() => navigate('/platform')}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-slate-800 dark:text-slate-100 border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 hover:border-blue-400 transition-colors"
                >
                  {t('landing.slideFive.cta.secondary', 'See how it works')}
                </button>
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default SlideFive;
